import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { 
  DataGrid, 
  GridToolbarContainer,
  GridToolbarFilterButton,
  GridColumnMenuFilterItem,
  GridColumnMenuHideItem,
  GridColumnMenuSortItem,
  getGridStringOperators,
  getGridNumericOperators,
} from '@mui/x-data-grid';
import { Box, Chip, Tooltip, Divider, Button, CircularProgress } from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import DashLoader from "../../components/DashLoader";
import {
  getSessionUsageSummary,
  getSessionUsageData,
  getUserGroupsBatch,
  getGroupsData,
  getGroupUsersData,
} from '../../apis/index';
import { exportToCSV } from '../../utils/exportUtils';
import { getLocalTimeZoneAbbr } from '../../utils/formatMessageTimestamp';
import "../../styles/Dashboard.css";
import "../../styles/EngagementDashboard.css";

const SessionDashboard = ({ selectedDatasetId, globalStartDate, globalEndDate, globalSelectedUsers }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Dashboard data state
  const [summaryData, setSummaryData] = useState(null);
  const [sessionsData, setSessionsData] = useState([]);
  
  // Pagination state
  const [paginationModel, setPaginationModel] = useState({
    page: 0, // MUI DataGrid is 0-indexed
    pageSize: 50,
  });
  const [totalRows, setTotalRows] = useState(0);
  const [dataLoading, setDataLoading] = useState(false);
  
  // Sort state
  const [sortModel, setSortModel] = useState([]);
  
  // Filter state
  const [filterModel, setFilterModel] = useState({ items: [] });

  // Resolved userIds when a groupName filter is active (null = inactive, [] = no match, [...] = filtered)
  const [groupFilterUserIds, setGroupFilterUserIds] = useState(null);

  // Debounce timer for filter fetch — avoids API calls on every keystroke
  const filterDebounceRef = useRef(null);

  // Export state
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [exportingAll, setExportingAll] = useState(false);
  const [exportProgress, setExportProgress] = useState('');
  const exportMenuRef = useRef(null);

  // Close export menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target)) {
        setExportMenuOpen(false);
      }
    };
    if (exportMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [exportMenuOpen]);

  // Format date to YYYY-MM-DD using local timezone components
  // (DatePicker returns local dates; toISOString() would shift the date for non-UTC timezones)
  const formatDateStr = (date) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Resolve a groupName filter to a list of userIds via user-service
  // Returns: [] if no groups match (no results), null on error (fallback to no filter)
  const resolveGroupFilter = async (value, operator) => {
    try {
      const groupsResponse = await getGroupsData();
      const allGroups = groupsResponse?.data?.data || [];
      const lowerValue = (value || '').toLowerCase().trim();

      const matchingGroups = allGroups.filter(group => {
        const name = (group.name || '').toLowerCase().trim();
        switch (operator) {
          case 'equals':     return name === lowerValue;
          case 'startsWith': return name.startsWith(lowerValue);
          case 'endsWith':   return name.endsWith(lowerValue);
          case 'contains':
          default:           return name.includes(lowerValue);
        }
      });

      if (matchingGroups.length === 0) return []; // no matching groups → no results

      // Fetch members for all matching groups in parallel
      const userIdSets = await Promise.all(
        matchingGroups.map(async group => {
          const usersResponse = await getGroupUsersData(group.id);
          const members = usersResponse?.data?.data || [];
          return members.map(m => m.userId).filter(Boolean);
        })
      );

      return [...new Set(userIdSets.flat())];
    } catch (err) {
      console.error('Failed to resolve group filter:', err);
      return null; // signal error — caller treats as no group filter
    }
  };

  // Fetch summary data (KPIs)
  const fetchSummaryData = async () => {
    try {
      const startDateStr = formatDateStr(globalStartDate);
      const endDateStr = formatDateStr(globalEndDate);
      const userIdsArray = globalSelectedUsers?.length > 0 ? globalSelectedUsers : null;
      
      const summary = await getSessionUsageSummary(startDateStr, endDateStr, undefined, selectedDatasetId, userIdsArray);
      setSummaryData(summary?.data?.data);
    } catch (err) {
      console.error('Failed to fetch session summary:', err);
    }
  };

  // Fetch paginated session data with sort and filter
  // groupUserIds: null = no group filter, [] = group has no members (return empty), [...] = filter by these userIds
  const fetchSessionData = useCallback(async (page, pageSize, sort = null, filter = null, groupUserIds = null) => {
    setDataLoading(true);
    try {
      const startDateStr = formatDateStr(globalStartDate);
      const endDateStr = formatDateStr(globalEndDate);

      // Compute effective userIds:
      // Group filter takes precedence; intersect with globalSelectedUsers if both active
      let effectiveUserIds;
      if (groupUserIds !== null) {
        if (globalSelectedUsers?.length > 0) {
          const globalSet = new Set(globalSelectedUsers);
          effectiveUserIds = groupUserIds.filter(id => globalSet.has(id));
        } else {
          effectiveUserIds = groupUserIds;
        }
        // Group filter active but no matching users — short-circuit, return empty
        if (effectiveUserIds.length === 0) {
          setSessionsData([]);
          setTotalRows(0);
          return;
        }
      } else {
        effectiveUserIds = globalSelectedUsers?.length > 0 ? globalSelectedUsers : null;
      }
      
      // Extract sort parameters
      const sortField = sort && sort.length > 0 ? sort[0].field : null;
      const sortOrder = sort && sort.length > 0 ? sort[0].sort : null;
      
      // Extract filter parameters — groupName is handled via effectiveUserIds, not as a server filter
      const filterItem = filter && filter.items && filter.items.length > 0 ? filter.items[0] : null;
      const filterField = (filterItem?.field && filterItem.field !== 'groupName') ? filterItem.field : null;
      const filterOperator = filterField ? (filterItem?.operator || null) : null;
      let filterValue = filterField ? filterItem?.value : null;
      
      // Format date filter value if it's a Date object
      if (filterField === 'date' && filterValue instanceof Date) {
        filterValue = filterValue.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      } else {
        filterValue = filterValue || null;
      }
      
      const response = await getSessionUsageData(
        startDateStr, 
        endDateStr, 
        undefined, 
        selectedDatasetId, 
        effectiveUserIds,
        page + 1, // API is 1-indexed, MUI is 0-indexed
        pageSize,
        null, // search
        sortField,
        sortOrder,
        filterField,
        filterOperator,
        filterValue
      );
      
      const data = response?.data?.data || [];
      const pagination = response?.data?.pagination || {};
      
      // Hybrid fallback: new records have groupName from DB;
      // only call getUserGroupsBatch for older records missing it
      const missingGroupRows = data.filter(row => !row.groupName && row.userId);
      const missingUserIds = [...new Set(missingGroupRows.map(row => row.userId))];

      let fallbackGroupsMap = {};
      if (missingUserIds.length > 0) {
        const groupsResponse = await getUserGroupsBatch(missingUserIds);
        if (groupsResponse?.data?.success) {
          fallbackGroupsMap = groupsResponse.data.data || {};
        }
      }

      const enrichedData = data.map(row => ({
        ...row,
        groupName: row.groupName
          || fallbackGroupsMap[row.userId]?.map(g => g.name).join(', ')
          || '-'
      }));

      setSessionsData(enrichedData);
      setTotalRows(pagination.totalRecords || data.length);
    } catch (err) {
      console.error('Failed to fetch session data:', err);
      setSessionsData([]);
      setTotalRows(0);
    } finally {
      setDataLoading(false);
    }
  }, [globalStartDate, globalEndDate, selectedDatasetId, globalSelectedUsers]);

  // Initial data fetch
  const fetchDashboardData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Fetch summary and initial page of data in parallel
      await Promise.all([
        fetchSummaryData(),
        fetchSessionData(0, paginationModel.pageSize, sortModel, filterModel, groupFilterUserIds)
      ]);
    } catch (err) {
      console.error('Failed to fetch session metrics:', err);
      setError('Failed to load session usage data. Please check if the metrics API is configured and running.');
    } finally {
      setLoading(false);
    }
  };

  // Handle pagination change
  const handlePaginationModelChange = (newModel) => {
    setPaginationModel(newModel);
    fetchSessionData(newModel.page, newModel.pageSize, sortModel, filterModel, groupFilterUserIds);
  };

  // Handle sort change (server-side)
  const handleSortModelChange = (newSortModel) => {
    setSortModel(newSortModel);
    // Reset to first page when sorting changes
    setPaginationModel(prev => ({ ...prev, page: 0 }));
    fetchSessionData(0, paginationModel.pageSize, newSortModel, filterModel, groupFilterUserIds);
  };

  // Handle filter change — groupName filter resolved to userIds, all others go server-side
  // filterModel updates immediately (controlled input); fetch is debounced by 600ms
  const handleFilterModelChange = (newFilterModel) => {
    setFilterModel(newFilterModel);
    setPaginationModel(prev => ({ ...prev, page: 0 }));

    // Clear any pending debounced fetch
    if (filterDebounceRef.current) clearTimeout(filterDebounceRef.current);

    const filterItem = newFilterModel.items?.[0];
    const hasValue = Boolean(filterItem?.value);

    // If filter is cleared, fetch immediately without delay
    if (!hasValue) {
      setGroupFilterUserIds(null);
      fetchSessionData(0, paginationModel.pageSize, sortModel, newFilterModel, null);
      return;
    }

    // Show loader immediately during the debounce window
    setDataLoading(true);

    // Otherwise debounce the fetch so it fires only after typing stops
    filterDebounceRef.current = setTimeout(async () => {
      if (filterItem?.field === 'groupName') {
        const resolvedUserIds = await resolveGroupFilter(filterItem.value, filterItem.operator || 'equals');
        setGroupFilterUserIds(resolvedUserIds);
        fetchSessionData(0, paginationModel.pageSize, sortModel, newFilterModel, resolvedUserIds);
      } else {
        setGroupFilterUserIds(null);
        fetchSessionData(0, paginationModel.pageSize, sortModel, newFilterModel, null);
      }
    }, 600);
  };

  // Build common export metadata from current filter state
  const buildExportMetadata = () => {
    const meta = { title: 'Session Usage Report' };
    if (globalStartDate && globalEndDate) {
      meta.dateRange = `${formatDateStr(globalStartDate)} to ${formatDateStr(globalEndDate)}`;
    }
    return meta;
  };

  // Format a row for CSV export (flatten & label columns)
  const formatRowForExport = (row) => {
    let dateStr = '-';
    let timeStr = '-';
    if (row.date) {
      const d = new Date(row.date);
      dateStr = d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
      const tz = getLocalTimeZoneAbbr(d);
      timeStr = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
      if (tz) timeStr += ` ${tz}`;
    }
    return {
    Date: dateStr,
    Time: timeStr,
    Username: row.userName || '-',
    Company: row.groupName || '-',
    Sessions: row.sessionCount || 0,
    'Session ID': row.sessionId || '-',
    Questions: row.questionCount || 0,
    'Human-in-loop Ref.': Array.isArray(row.humanInLoopRefs) ? row.humanInLoopRefs.join(', ') : '-',
  };
  };

  // Export current page
  const handleExportCurrentPage = () => {
    setExportMenuOpen(false);
    const rows = sessionsData.map(formatRowForExport);
    exportToCSV(rows, 'Innferre_SessionUsage', buildExportMetadata());
  };

  // Export all pages — fetches every page with current filters/sort then exports
  const handleExportAllPages = async () => {
    setExportMenuOpen(false);
    if (totalRows === 0) return;

    setExportingAll(true);
    setExportProgress('Preparing export...');

    const MAX_PAGE_SIZE = 100;
    const totalPages = Math.ceil(totalRows / MAX_PAGE_SIZE);
    let allRows = [];

    try {
      const startDateStr = formatDateStr(globalStartDate);
      const endDateStr = formatDateStr(globalEndDate);

      // Compute effective userIds (same logic as fetchSessionData)
      let effectiveUserIds;
      if (groupFilterUserIds !== null) {
        if (globalSelectedUsers?.length > 0) {
          const globalSet = new Set(globalSelectedUsers);
          effectiveUserIds = groupFilterUserIds.filter(id => globalSet.has(id));
        } else {
          effectiveUserIds = groupFilterUserIds;
        }
        if (effectiveUserIds.length === 0) {
          setExportingAll(false);
          setExportProgress('');
          return;
        }
      } else {
        effectiveUserIds = globalSelectedUsers?.length > 0 ? globalSelectedUsers : null;
      }

      // Extract current sort params
      const sortField = sortModel.length > 0 ? sortModel[0].field : null;
      const sortOrder = sortModel.length > 0 ? sortModel[0].sort : null;

      // Extract current filter params (excluding groupName which is handled via userIds)
      const filterItem = filterModel.items?.length > 0 ? filterModel.items[0] : null;
      const filterField = (filterItem?.field && filterItem.field !== 'groupName') ? filterItem.field : null;
      const filterOperator = filterField ? (filterItem?.operator || null) : null;
      let filterValue = filterField ? filterItem?.value : null;
      if (filterField === 'date' && filterValue instanceof Date) {
        filterValue = filterValue.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
      } else {
        filterValue = filterValue || null;
      }

      // Fetch pages in batches of 3 to avoid overwhelming the API
      const BATCH_SIZE = 3;
      for (let batchStart = 0; batchStart < totalPages; batchStart += BATCH_SIZE) {
        const batchEnd = Math.min(batchStart + BATCH_SIZE, totalPages);
        const pagePromises = [];

        for (let p = batchStart; p < batchEnd; p++) {
          pagePromises.push(
            getSessionUsageData(
              startDateStr, endDateStr, undefined, selectedDatasetId, effectiveUserIds,
              p + 1, MAX_PAGE_SIZE, null,
              sortField, sortOrder,
              filterField, filterOperator, filterValue
            )
          );
        }

        setExportProgress(`Fetching page ${batchStart + 1}–${batchEnd} of ${totalPages}...`);
        const responses = await Promise.all(pagePromises);

        for (const response of responses) {
          const data = response?.data?.data || [];

          // Enrich with groupName fallback (same as fetchSessionData)
          const missingGroupRows = data.filter(row => !row.groupName && row.userId);
          const missingUserIds = [...new Set(missingGroupRows.map(row => row.userId))];

          let fallbackGroupsMap = {};
          if (missingUserIds.length > 0) {
            try {
              const groupsResponse = await getUserGroupsBatch(missingUserIds);
              if (groupsResponse?.data?.success) {
                fallbackGroupsMap = groupsResponse.data.data || {};
              }
            } catch { /* proceed without group names */ }
          }

          const enriched = data.map(row => ({
            ...row,
            groupName: row.groupName
              || fallbackGroupsMap[row.userId]?.map(g => g.name).join(', ')
              || '-'
          }));

          allRows = allRows.concat(enriched);
        }
      }

      setExportProgress('Generating CSV...');
      const rows = allRows.map(formatRowForExport);
      exportToCSV(rows, 'Innferre_SessionUsage_All', buildExportMetadata());
    } catch (err) {
      console.error('Failed to export all pages:', err);
    } finally {
      setExportingAll(false);
      setExportProgress('');
    }
  };

  useEffect(() => {
    // Only fetch data when both dates are set (for range picker) and valid
    if (globalStartDate && globalEndDate && globalStartDate <= globalEndDate) {
      fetchDashboardData();
    }
  }, [globalStartDate?.getTime(), globalEndDate?.getTime(), selectedDatasetId, JSON.stringify(globalSelectedUsers)]);

  // Format numbers for display
  const formatNumber = (num) => {
    if (!num) return '0';
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  const formatCost = (cost) => '$' + (cost || 0).toFixed(4);

  // Render summary cards - Same styling as Token Dashboard
  const renderSummaryCards = () => {
    const cards = [
      {
        title: 'Total Cost',
        value: '$' + Math.round(summaryData?.totalCost || 0),
        subtitle: (
          <>
            <span className="kpi-param-label">Avg / session:</span>{' '}
            <span className="kpi-param-value">
              {'$' + Math.round((summaryData?.totalCost || 0) / (summaryData?.totalSessions || 1))}
            </span>
          </>
        ),
      },
      {
        title: 'Total Sessions',
        value: formatNumber(summaryData?.totalSessions),
        subtitle: (
          <>
            <span className="kpi-param-label">Avg Questions:</span>{' '}
            <span className="kpi-param-value">
              {((summaryData?.totalQuestions || 0) / (summaryData?.totalSessions || 1)).toFixed(1)}
            </span>
          </>
        ),
      },
      {
        title: 'Unique Users',
        value: formatNumber(summaryData?.uniqueUsers),
        subtitle: (
          <>
            <span className="kpi-param-label">Sessions/user:</span>{' '}
            <span className="kpi-param-value">
              {((summaryData?.totalSessions || 0) / (summaryData?.uniqueUsers || 1)).toFixed(1)}
            </span>
          </>
        ),
      },
    ];

    return (
      <div className="dashboard-cards session-kpi-cards">
        {cards.map((card, index) => (
          <div 
            key={index} 
            className="card_dashboard simple-kpi-card"
          >
            <p className="kpi-title">{card.title}</p>
            <h3 className="kpi-value kpi-value-primary">
              {loading ? '...' : card.value}
            </h3>
            {card.subtitle && <p className="kpi-subtitle">{card.subtitle}</p>}
          </div>
        ))}
      </div>
    );
  };

  // DataGrid columns configuration
  const columns = useMemo(() => [
    {
      field: 'date',
      headerName: 'Date',
      type: 'date',
      flex: 0.9,
      minWidth: 120,
      filterable: false,
      valueGetter: (params) => {
        return params.value ? new Date(params.value) : null;
      },
      renderCell: (params) => {
        if (!params.value) return <span style={{ color: '#97A3BA', fontFamily: "'Satoshi', sans-serif", fontSize: '14px' }}>-</span>;
        const raw = params.row?.date || params.value;
        const d = new Date(raw);
        const formatted = d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
        return <span style={{ color: '#14181F', fontFamily: "'Satoshi', sans-serif", fontSize: '14px' }}>{formatted}</span>;
      },
    },
    {
      field: 'time',
      headerName: 'Time',
      flex: 0.8,
      minWidth: 110,
      filterable: false,
      sortable: false,
      valueGetter: (params) => {
        return params.row?.date ? new Date(params.row.date) : null;
      },
      renderCell: (params) => {
        if (!params.value) return <span style={{ color: '#97A3BA', fontFamily: "'Satoshi', sans-serif", fontSize: '14px' }}>-</span>;
        const d = params.value;
        const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
        const tz = getLocalTimeZoneAbbr(d);
        return <span style={{ color: '#14181F', fontFamily: "'Satoshi', sans-serif", fontSize: '14px' }}>{tz ? `${time} ${tz}` : time}</span>;
      },
    },
    {
      field: 'userName',
      headerName: 'Username',
      flex: 1,
      minWidth: 130,
      sortable: false,
      filterOperators: getGridStringOperators().filter(op => ['equals'].includes(op.value)),
      renderCell: (params) => (
        <span style={{ color: '#14181F', fontFamily: "'Satoshi', sans-serif", fontSize: '14px', fontWeight: '500' }}>
          {params.value || '-'}
        </span>
      ),
    },
    {
      field: 'groupName',
      headerName: 'Company',
      flex: 1,
      minWidth: 120,
      sortable: false,
      filterOperators: getGridStringOperators().filter(op => ['equals'].includes(op.value)),
      renderCell: (params) => {
        const value = params.value || 'N/A';
        return (
          <Tooltip
            title={value}
            arrow
            placement="top"
            enterDelay={300}
            componentsProps={{
              tooltip: {
                sx: {
                  backgroundColor: '#1F2937',
                  color: '#fff',
                  fontFamily: "'Satoshi', sans-serif",
                  fontSize: '12px',
                  '& .MuiTooltip-arrow': {
                    color: '#1F2937',
                  },
                },
              },
            }}
          >
            <Chip
              label={value}
              size="small"
              variant="outlined"
              sx={{
                fontFamily: "'Satoshi', sans-serif",
                fontSize: '12px',
                height: '24px',
                borderColor: '#e0e0e0',
                color: '#14181F',
                cursor: 'pointer',
              }}
            />
          </Tooltip>
        );
      },
    },
    {
      field: 'sessionCount',
      headerName: 'Sessions',
      flex: 0.5,
      minWidth: 80,
      type: 'number',
      headerAlign: 'center',
      align: 'center',
      filterOperators: getGridNumericOperators().filter(op => ['=', '>=', '<='].includes(op.value)),
      renderCell: (params) => (
        <span style={{ fontWeight: '600', color: '#0053ED', fontFamily: "'Satoshi', sans-serif", fontSize: '14px' }}>
          {params.value || 0}
        </span>
      ),
    },
    {
      field: 'sessionId',
      headerName: 'Session ID',
      flex: 1.5,
      minWidth: 220,
      sortable: false,
      filterOperators: getGridStringOperators().filter(op => ['equals'].includes(op.value)),
      renderCell: (params) => {
        const rowDatasetId = params.row.datasetId || selectedDatasetId;
        const sessionId = params.value;
        if (sessionId && rowDatasetId) {
          return (
            <Tooltip title="View conversation in AI Assistant">
              <span
                style={{ 
                  color: '#0053ED', 
                  fontSize: '13px',
                  fontFamily: "'Satoshi', sans-serif",
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                }}
                onClick={() => window.open(`/aiassistant?c=${rowDatasetId}&cid=${sessionId}`, '_blank')}
              >
                {sessionId}
              </span>
            </Tooltip>
          );
        }
        return (
          <span style={{ 
            color: '#14181F', 
            fontSize: '13px',
            fontFamily: "'Satoshi', sans-serif",
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}>
            {sessionId || '-'}
          </span>
        );
      },
    },
    {
      field: 'questionCount',
      headerName: 'Questions',
      flex: 0.6,
      minWidth: 90,
      type: 'number',
      headerAlign: 'center',
      align: 'center',
      filterOperators: getGridNumericOperators().filter(op => ['=', '>=', '<='].includes(op.value)),
      renderCell: (params) => (
        <span style={{ fontWeight: '600', color: '#0053ED', fontFamily: "'Satoshi', sans-serif", fontSize: '14px' }}>
          {params.value || 0}
        </span>
      ),
    },
    {
      field: 'humanInLoopRefs',
      headerName: 'Human-in-loop Ref.',
      flex: 1.2,
      minWidth: 180,
      sortable: false,
      filterOperators: getGridStringOperators().filter(op => ['equals'].includes(op.value)),
      renderCell: (params) => {
        const refs = params.value || [];
        if (refs.length === 0) {
          return <span style={{ color: '#97A3BA', fontSize: '14px', fontFamily: "'Satoshi', sans-serif" }}>-</span>;
        }
        const displayText = refs.join(', ');
        return (
          <Tooltip
            title={
              <Box sx={{ 
                p: 1, 
                fontFamily: "'Satoshi', sans-serif", 
                fontSize: '13px',
                userSelect: 'text',
                cursor: 'text',
              }}>
                {refs.map((ref, index) => (
                  <div key={index} style={{ padding: '2px 0' }}>{ref}</div>
                ))}
              </Box>
            }
            arrow
            placement="top-start"
            enterDelay={100}
            leaveDelay={100}
            disableInteractive={false}
            componentsProps={{
              tooltip: {
                sx: {
                  backgroundColor: '#1F2937',
                  color: '#fff',
                  maxWidth: 300,
                  '& .MuiTooltip-arrow': {
                    color: '#1F2937',
                  },
                },
              },
            }}
          >
            <span 
              style={{ 
                color: '#0053ED', 
                fontSize: '13px',
                fontFamily: "'Satoshi', sans-serif",
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '100%',
                display: 'block',
                cursor: 'pointer',
              }}
            >
              {displayText}
            </span>
          </Tooltip>
        );
      },
    },
  ], []);

  return (
    <div className="dashboard">
      {/* Error Message */}
      {error && (
        <div style={{
          gridColumn: '1 / -1',
          background: '#FEE2E2',
          color: '#DC2626',
          padding: '12px 16px',
          borderRadius: '8px',
          marginBottom: '15px',
        }}>
          ⚠️ {error}
        </div>
      )}

      {/* Summary Cards */}
      {renderSummaryCards()}

      {/* Sessions Table with MUI DataGrid */}
      <div className="card_dashboard" style={{ position: 'relative', gridColumn: '1 / -1' }}>
        <h3 style={{ fontSize: '17px', fontWeight: '700', marginBottom: '18px', color: '#1F2937' }}>
          Session Usage Details
        </h3>
        {loading ? (
          <DashLoader />
        ) : (
          <Box sx={{ 
            width: '100%',
            fontFamily: "'Satoshi', sans-serif",
            
            // DataGrid root styling
            '& .MuiDataGrid-root': {
              border: 'none',
              fontFamily: "'Satoshi', sans-serif",
            },
            '& .MuiDataGrid-cell': {
              borderBottom: 'none',
              fontFamily: "'Satoshi', sans-serif",
              fontSize: '14px',
              color: '#14181F',
              padding: '12px',
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: 'transparent',
              border: 'none',
              borderBottom: 'none',
            },
            '& .MuiDataGrid-columnHeader': {
              padding: '12px',
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: '600',
              color: '#14181F',
              fontSize: '14px',
              fontFamily: "'Satoshi', sans-serif",
            },
            '& .MuiDataGrid-columnSeparator': {
              display: 'none',
            },
            // Remove column header blue selection effect
            '& .MuiDataGrid-columnHeader:focus': {
              outline: 'none !important',
              backgroundColor: 'transparent !important',
            },
            '& .MuiDataGrid-columnHeader:focus-within': {
              outline: 'none !important',
              backgroundColor: 'transparent !important',
            },
            '& .MuiDataGrid-columnHeader:hover': {
              backgroundColor: 'transparent',
            },
            // Column header styling
            '& .MuiDataGrid-columnHeaderTitleContainer': {
              cursor: 'default',
            },
            '& .MuiDataGrid-columnHeaderTitleContainerContent': {
              cursor: 'default',
            },
            // Hide sort icon completely
            '& .MuiDataGrid-iconButtonContainer': {
              display: 'none',
            },
            '& .MuiDataGrid-sortIcon': {
              display: 'none',
            },
            // Show menu icon on hover
            '& .MuiDataGrid-columnHeader:hover .MuiDataGrid-menuIcon': {
              visibility: 'visible',
            },
            '& .MuiDataGrid-columnHeader .MuiDataGrid-menuIcon': {
              visibility: 'hidden',
            },
            // Hide menu icon tooltip
            '& .MuiDataGrid-menuIconButton': {
              '& .MuiTooltip-popper': {
                display: 'none',
              },
            },
            '& .MuiDataGrid-cell:focus': {
              outline: 'none',
            },
            '& .MuiDataGrid-cell:focus-within': {
              outline: 'none',
            },
            // Remove row selection styling completely
            '& .MuiDataGrid-row.Mui-selected': {
              backgroundColor: 'transparent !important',
            },
            '& .MuiDataGrid-row.Mui-selected:hover': {
              backgroundColor: '#f8fafc !important',
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: '#f8fafc',
            },
            '& .MuiDataGrid-footerContainer': {
              borderTop: 'none',
              fontFamily: "'Satoshi', sans-serif",
            },
            '& .MuiDataGrid-virtualScroller': {
              borderTop: 'none',
            },
            
            // Toolbar container
            '& .MuiDataGrid-toolbarContainer': {
              padding: '12px 8px 16px 8px',
              gap: '12px',
              flexWrap: 'wrap',
            },
            
            // Toolbar buttons (Filters, Export)
            '& .MuiDataGrid-toolbarContainer .MuiButton-root': {
              fontFamily: "'Satoshi', sans-serif",
              fontSize: '13px',
              fontWeight: '500',
              color: '#14181F',
              textTransform: 'none',
              padding: '6px 12px',
              borderRadius: '6px',
              border: '1px solid #e0e0e0',
              backgroundColor: '#fff',
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: '#F4F4F6',
                borderColor: '#0053ED',
                color: '#0053ED',
              },
              '& .MuiButton-startIcon': {
                color: '#97A3BA',
              },
              '&:hover .MuiButton-startIcon': {
                color: '#0053ED',
              },
            },
            
            // Quick filter input
            '& .MuiDataGrid-toolbarContainer .MuiTextField-root': {
              '& .MuiInputBase-root': {
                fontFamily: "'Satoshi', sans-serif",
                fontSize: '13px',
                borderRadius: '6px',
                backgroundColor: '#fff',
                '& fieldset': {
                  borderColor: '#e0e0e0',
                },
                '&:hover fieldset': {
                  borderColor: '#0053ED',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#0053ED',
                  borderWidth: '1px',
                },
              },
              '& .MuiInputBase-input': {
                padding: '8px 12px',
                color: '#14181F',
                '&::placeholder': {
                  color: '#999',
                  opacity: 1,
                },
              },
            },
            
            // Pagination styling
            '& .MuiTablePagination-root': {
              fontFamily: "'Satoshi', sans-serif",
              color: '#14181F',
            },
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
              fontFamily: "'Satoshi', sans-serif",
              fontSize: '13px',
              color: '#97A3BA',
            },
            '& .MuiTablePagination-select': {
              fontFamily: "'Satoshi', sans-serif",
              fontSize: '13px',
            },
          }}>
          
          {/* Global styles for MUI Popper/Menu dropdowns */}
          <style>{`
            /* Base dropdown/popup styling to match app Semantic UI */
            .MuiPopper-root .MuiPaper-root,
            .MuiPopover-paper {
              font-family: 'Satoshi', sans-serif !important;
              border-radius: 8px !important;
              box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1) !important;
              border: 1px solid #F4F4F6 !important;
              margin-top: 0px !important;
            }
            
            /* All MUI Select dropdowns - match app style */
            .MuiSelect-select {
              font-family: 'Satoshi', sans-serif !important;
              font-size: 14px !important;
              color: #14181F !important;
            }
            .MuiSelect-icon {
              color: #97A3BA !important;
            }
            
            /* Dropdown menu paper */
            .MuiMenu-paper {
              font-family: 'Satoshi', sans-serif !important;
              border-radius: 8px !important;
              box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1) !important;
              border: 1px solid #F4F4F6 !important;
              margin-top: 0px !important;
            }
            
            /* Menu list */
            .MuiMenu-list {
              padding: 8px !important;
            }
            .MuiMenu-list .MuiMenuItem-root {
              font-family: 'Satoshi', sans-serif !important;
              font-size: 14px !important;
              color: #14181F !important;
              padding: 10px 14px !important;
              border-radius: 6px !important;
              margin: 2px 0 !important;
            }
            .MuiMenu-list .MuiMenuItem-root:hover {
              background-color: #F4F4F6 !important;
            }
            .MuiMenu-list .MuiMenuItem-root.Mui-selected {
              background-color: rgba(0, 83, 237, 0.08) !important;
              color: #0053ED !important;
            }
            .MuiMenu-list .MuiMenuItem-root.Mui-selected:hover {
              background-color: rgba(0, 83, 237, 0.12) !important;
            }
            
            /* Dropdown panel styling for Filters, Export */
            .MuiDataGrid-panel {
              font-family: 'Satoshi', sans-serif !important;
            }
            .MuiDataGrid-panel.MuiPopper-root[data-popper-placement] {
              margin-top: -35px !important;
            }
            .MuiDataGrid-panel .MuiPaper-root {
              font-family: 'Satoshi', sans-serif !important;
              border-radius: 8px !important;
              box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1) !important;
              border: 1px solid #F4F4F6 !important;
            }
            .MuiDataGrid-panelHeader {
              padding: 14px 16px !important;
              border-bottom: 1px solid #F4F4F6 !important;
            }
            .MuiDataGrid-panelContent {
              padding: 12px !important;
            }
            .MuiDataGrid-panelFooter {
              padding: 10px 16px !important;
              border-top: 1px solid #F4F4F6 !important;
            }
            
            /* Sort menu items styling */
            .MuiDataGrid-menuList {
              padding: 8px !important;
            }
            .MuiDataGrid-menuList .MuiMenuItem-root {
              font-family: 'Satoshi', sans-serif !important;
              font-size: 14px !important;
              color: #14181F !important;
              padding: 10px 16px !important;
              border-radius: 6px !important;
              margin: 2px !important;
            }
            .MuiDataGrid-menuList .MuiMenuItem-root:hover {
              background-color: #F4F4F6 !important;
            }
            .MuiDataGrid-menuList .MuiListItemIcon-root {
              color: #97A3BA !important;
              min-width: 32px !important;
            }
            
            /* Filter panel styling */
            .MuiDataGrid-filterForm {
              padding: 16px !important;
              gap: 12px !important;
            }
            .MuiDataGrid-filterFormColumnInput,
            .MuiDataGrid-filterFormOperatorInput,
            .MuiDataGrid-filterFormValueInput {
              width: auto !important;
              min-width: 150px !important;
            }
            
            /* Filter form dropdown inputs - match app Semantic UI style */
            .MuiDataGrid-filterForm .MuiInputBase-root {
              font-family: 'Satoshi', sans-serif !important;
              font-size: 14px !important;
              border-radius: 8px !important;
              background-color: #fff !important;
              min-height: 44px !important;
            }
            .MuiDataGrid-filterForm .MuiOutlinedInput-root {
              border-radius: 8px !important;
            }
            .MuiDataGrid-filterForm .MuiOutlinedInput-notchedOutline {
              border: 1px solid #e0e0e0 !important;
              border-radius: 8px !important;
            }
            .MuiDataGrid-filterForm .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline {
              border-color: #0053ED !important;
              background-color: transparent !important;
            }
            .MuiDataGrid-filterForm .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
              border: 2px solid #0053ED !important;
            }
            .MuiDataGrid-filterForm .MuiSelect-select {
              font-family: 'Satoshi', sans-serif !important;
              font-size: 14px !important;
              color: #14181F !important;
              padding: 12px 14px !important;
            }
            .MuiDataGrid-filterForm .MuiInputBase-input {
              font-family: 'Satoshi', sans-serif !important;
              font-size: 14px !important;
              color: #14181F !important;
              padding: 12px 14px !important;
            }
            .MuiDataGrid-filterForm .MuiInputLabel-root {
              font-family: 'Satoshi', sans-serif !important;
              font-size: 14px !important;
              color: #97A3BA !important;
            }
            .MuiDataGrid-filterForm .MuiInputLabel-root.Mui-focused {
              color: #0053ED !important;
            }
            .MuiDataGrid-filterForm .MuiSelect-icon {
              color: #97A3BA !important;
              right: 12px !important;
            }
            
            /* Filter dropdown menu items */
            .MuiDataGrid-panel .MuiFormControl-root {
              font-family: 'Satoshi', sans-serif !important;
            }
            .MuiDataGrid-panel .MuiInputBase-root {
              font-family: 'Satoshi', sans-serif !important;
              font-size: 14px !important;
              border-radius: 8px !important;
            }
            .MuiDataGrid-panel .MuiInputLabel-root {
              font-family: 'Satoshi', sans-serif !important;
              font-size: 14px !important;
              color: #97A3BA !important;
            }
            .MuiDataGrid-panel .MuiSelect-select {
              font-family: 'Satoshi', sans-serif !important;
              font-size: 14px !important;
              color: #14181F !important;
            }
            .MuiDataGrid-panel .MuiMenuItem-root {
              font-family: 'Satoshi', sans-serif !important;
              font-size: 14px !important;
              color: #14181F !important;
              padding: 10px 16px !important;
            }
            .MuiDataGrid-panel .MuiMenuItem-root:hover {
              background-color: #F4F4F6 !important;
            }
            .MuiDataGrid-panel .MuiMenuItem-root.Mui-selected {
              background-color: rgba(0, 83, 237, 0.08) !important;
              color: #0053ED !important;
            }
            .MuiDataGrid-panel .MuiMenuItem-root.Mui-selected:hover {
              background-color: rgba(0, 83, 237, 0.12) !important;
            }
            
            /* Columns panel checkbox styling */
            .MuiDataGrid-columnsPanel {
              padding: 8px !important;
            }
            .MuiDataGrid-columnsPanelRow {
              padding: 4px 8px !important;
            }
            .MuiDataGrid-panel .MuiFormControlLabel-root {
              font-family: 'Satoshi', sans-serif !important;
            }
            .MuiDataGrid-panel .MuiFormControlLabel-label {
              font-family: 'Satoshi', sans-serif !important;
              font-size: 13px !important;
              color: #14181F !important;
            }
            .MuiDataGrid-panel .MuiCheckbox-root {
              color: #999 !important;
            }
            .MuiDataGrid-panel .MuiCheckbox-root.Mui-checked {
              color: #0053ED !important;
            }
            
            /* List styling in panels */
            .MuiDataGrid-panel .MuiList-root {
              padding: 4px !important;
            }
            .MuiDataGrid-panel .MuiListItemIcon-root {
              color: #97A3BA !important;
              min-width: 36px !important;
            }
            .MuiDataGrid-panel .MuiListItemText-primary {
              font-family: 'Satoshi', sans-serif !important;
              font-size: 14px !important;
              color: #14181F !important;
            }
            
            /* Button styling in panels */
            .MuiDataGrid-panel .MuiButton-root {
              font-family: 'Satoshi', sans-serif !important;
              font-size: 14px !important;
              text-transform: none !important;
              border-radius: 6px !important;
            }
            .MuiDataGrid-panel .MuiButton-textPrimary {
              color: #0053ED !important;
            }
            .MuiDataGrid-panel .MuiButton-textPrimary:hover {
              background-color: rgba(0, 83, 237, 0.08) !important;
            }
            
            /* Input focus states in panels */
            .MuiDataGrid-panel .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
              border-color: #0053ED !important;
              border-width: 2px !important;
            }
            .MuiDataGrid-panel .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline {
              border-color: #0053ED !important;
            }
            
            /* Switch/Toggle styling */
            .MuiDataGrid-panel .MuiSwitch-switchBase.Mui-checked {
              color: #0053ED !important;
            }
            .MuiDataGrid-panel .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track {
              background-color: #0053ED !important;
            }
            
            /* Column menu (three dots) dropdown styling */
            .MuiDataGrid-menu .MuiPaper-root {
              font-family: 'Satoshi', sans-serif !important;
              border-radius: 8px !important;
              box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1) !important;
              border: 1px solid #F4F4F6 !important;
              padding: 8px !important;
            }
            .MuiDataGrid-menu .MuiMenuItem-root {
              font-family: 'Satoshi', sans-serif !important;
              font-size: 14px !important;
              color: #14181F !important;
              padding: 10px 16px !important;
              border-radius: 6px !important;
              margin: 2px !important;
            }
            .MuiDataGrid-menu .MuiMenuItem-root:hover {
              background-color: #F4F4F6 !important;
            }
            .MuiDataGrid-menu .MuiListItemIcon-root {
              color: #97A3BA !important;
              min-width: 32px !important;
            }
            .MuiDataGrid-menu .MuiDivider-root {
              margin: 4px 8px !important;
              border-color: #F4F4F6 !important;
            }
            
            /* Pagination dropdown styling */
            .MuiTablePagination-select {
              font-family: 'Satoshi', sans-serif !important;
              font-size: 14px !important;
              color: #14181F !important;
              border-radius: 8px !important;
            }
            .MuiTablePagination-selectIcon {
              color: #97A3BA !important;
            }
            .MuiTablePagination-menuItem {
              font-family: 'Satoshi', sans-serif !important;
              font-size: 14px !important;
              color: #14181F !important;
            }
            .MuiTablePagination-menuItem:hover {
              background-color: #F4F4F6 !important;
            }
            .MuiTablePagination-menuItem.Mui-selected {
              background-color: rgba(0, 83, 237, 0.08) !important;
              color: #0053ED !important;
            }
            
            /* Autocomplete dropdown styling (for filter values) */
            .MuiAutocomplete-popper .MuiPaper-root {
              font-family: 'Satoshi', sans-serif !important;
              border-radius: 8px !important;
              box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1) !important;
              border: 1px solid #F4F4F6 !important;
            }
            .MuiAutocomplete-option {
              font-family: 'Satoshi', sans-serif !important;
              font-size: 14px !important;
              color: #14181F !important;
              padding: 10px 16px !important;
            }
            .MuiAutocomplete-option:hover {
              background-color: #F4F4F6 !important;
            }
            .MuiAutocomplete-option[aria-selected="true"] {
              background-color: rgba(0, 83, 237, 0.08) !important;
              color: #0053ED !important;
            }
            
            /* Filter form select dropdowns */
            .MuiDataGrid-filterFormColumnInput .MuiSelect-select,
            .MuiDataGrid-filterFormOperatorInput .MuiSelect-select,
            .MuiDataGrid-filterFormValueInput .MuiSelect-select {
              font-family: 'Satoshi', sans-serif !important;
              font-size: 14px !important;
              color: #14181F !important;
              padding: 12px 14px !important;
            }
            
            /* Remove default focus outlines on dropdowns */
            .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
              border-color: #0053ED !important;
              border-width: 2px !important;
            }
          `}</style>
            <DataGrid
              rows={sessionsData}
              columns={columns}
              paginationModel={paginationModel}
              onPaginationModelChange={handlePaginationModelChange}
              pageSizeOptions={[10, 25, 50, 100]}
              paginationMode="server"
              sortingMode="server"
              filterMode="server"
              sortModel={sortModel}
              onSortModelChange={handleSortModelChange}
              filterModel={filterModel}
              onFilterModelChange={handleFilterModelChange}
              rowCount={totalRows}
              loading={dataLoading}
              slots={{ 
                columnMenu: (props) => {
                  const field = props.colDef.field;
                  const showSort = ['date', 'sessionCount', 'questionCount'].includes(field);
                  const showHide = false;
                  return (
                    <Box sx={{ py: 0.5 }}>
                      {showSort && (
                        <>
                          <GridColumnMenuSortItem onClick={props.hideMenu} colDef={props.colDef} />
                          <Divider sx={{ my: 0.5 }} />
                        </>
                      )}
                      <GridColumnMenuFilterItem onClick={props.hideMenu} colDef={props.colDef} />
                      {showHide && (
                        <>
                          <Divider sx={{ my: 0.5 }} />
                          <GridColumnMenuHideItem onClick={props.hideMenu} colDef={props.colDef} />
                        </>
                      )}
                    </Box>
                  );
                },
                toolbar: () => (
                  <GridToolbarContainer>
                    <GridToolbarFilterButton />
                    <Box ref={exportMenuRef} sx={{ position: 'relative', display: 'inline-block' }}>
                      <Button
                        size="small"
                        startIcon={exportingAll ? <CircularProgress size={16} /> : <FileDownloadIcon />}
                        onClick={() => setExportMenuOpen(prev => !prev)}
                        disabled={exportingAll}
                      >
                        {exportingAll ? exportProgress : 'Export'}
                      </Button>
                      {exportMenuOpen && (
                        <div
                          style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            zIndex: 100,
                            marginTop: '4px',
                            minWidth: '230px',
                            background: '#fff',
                            border: '1px solid #e3e3e8',
                            borderRadius: '10px',
                            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
                            padding: '6px 0',
                            animation: 'chat-view-dropdown-in 0.15s ease-out',
                          }}
                        >
                          <div
                            className="export-dropdown-option"
                            onClick={handleExportCurrentPage}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '10px',
                              padding: '10px 14px',
                              cursor: 'pointer',
                              fontSize: '13px',
                              fontFamily: "'Satoshi', sans-serif",
                              color: '#374151',
                              transition: 'background 0.15s',
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = '#f5f7fa'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                          >
                            <DescriptionOutlinedIcon sx={{ fontSize: 17, color: '#97A3BA' }} />
                            Export Current Page
                          </div>
                          <div
                            style={{
                              height: '1px',
                              background: '#f0f0f0',
                              margin: '2px 10px',
                            }}
                          />
                          <div
                            className="export-dropdown-option"
                            onClick={totalRows > 0 ? handleExportAllPages : undefined}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '10px',
                              padding: '10px 14px',
                              cursor: totalRows === 0 ? 'default' : 'pointer',
                              fontSize: '13px',
                              fontFamily: "'Satoshi', sans-serif",
                              color: totalRows === 0 ? '#ccc' : '#374151',
                              transition: 'background 0.15s',
                            }}
                            onMouseEnter={(e) => { if (totalRows > 0) e.currentTarget.style.background = '#f5f7fa'; }}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                          >
                            <CloudDownloadOutlinedIcon sx={{ fontSize: 17, color: totalRows === 0 ? '#ddd' : '#97A3BA' }} />
                            Export All Pages {totalRows > 0 ? `(${totalRows})` : ''}
                          </div>
                        </div>
                      )}
                    </Box>
                  </GridToolbarContainer>
                ),
              }}
              disableRowSelectionOnClick
              autoHeight
              initialState={{
                sorting: {
                  sortModel: [{ field: 'date', sort: 'desc' }],
                },
              }}
              sx={{
                minHeight: 400,
                '& .MuiDataGrid-virtualScroller': {
                  minHeight: 300,
                },
                '& .MuiDataGrid-row.Mui-selected': {
                  backgroundColor: 'transparent !important',
                },
                '& .MuiDataGrid-row.Mui-selected:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04) !important',
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
                '& .MuiDataGrid-cell:focus': {
                  outline: 'none',
                },
                '& .MuiDataGrid-cell:focus-within': {
                  outline: 'none',
                },
                '& .MuiDataGrid-columnHeader:focus': {
                  outline: 'none',
                },
                '& .MuiDataGrid-columnHeader:focus-within': {
                  outline: 'none',
                },
              }}
              localeText={{
                noRowsLabel: 'No session data available',
                toolbarFilters: 'Filters',
                toolbarExport: 'Export',
              }}
            />
          </Box>
        )}
      </div>
    </div>
  );
};

export default SessionDashboard;
